import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { VideoPlayer } from "../components/VideoPlayer";
import { ChannelZap } from "../components/ChannelZap";
import {
  Radio, SkipForward, SkipBack, Shuffle, Pin, PinOff,
  Volume2, VolumeX, Loader2, Tv,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Continuous cross-channel TV stream. Random shuffle of all channels' videos,
 * auto-advance, optional channel-pin to lock the stream to one source.
 */
export default function LiveStream() {
  const [allChannels, setAllChannels] = useState([]);
  const [playlist, setPlaylist] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pinnedChannel, setPinnedChannel] = useState(null);  // channel id or null
  const [muted, setMuted] = useState(false);
  const fetchedAt = useRef(0);

  const fetchPlaylist = useCallback((pinned = null) => {
    const params = pinned ? `?channel_ids=${pinned}&limit=200` : `?limit=200`;
    return api
      .get(`/livestream/playlist${params}`)
      .then(r => {
        setAllChannels(r.data.channels || []);
        setPlaylist(r.data.playlist || []);
        setIndex(0);
        fetchedAt.current = Date.now();
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchPlaylist();
  }, [fetchPlaylist]);

  const current = playlist[index];
  const nextUp = playlist[index + 1];

  const skipNext = useCallback(() => {
    if (playlist.length === 0) return;
    if (index >= playlist.length - 1) {
      // Reached the end -> fetch a fresh shuffle
      setLoading(true);
      fetchPlaylist(pinnedChannel);
    } else {
      setIndex(i => i + 1);
    }
  }, [index, playlist.length, fetchPlaylist, pinnedChannel]);

  const skipBack = () => setIndex(i => Math.max(0, i - 1));

  const reshuffle = useCallback(() => {
    toast.success("Mélange en cours...");
    setLoading(true);
    fetchPlaylist(pinnedChannel);
  }, [fetchPlaylist, pinnedChannel]);

  const togglePin = (chId) => {
    if (pinnedChannel === chId) {
      setPinnedChannel(null);
      toast("Pin retiré · diffusion toutes chaînes", { icon: "📺" });
      setLoading(true);
      fetchPlaylist(null);
    } else {
      setPinnedChannel(chId);
      const ch = allChannels.find(c => c.id === chId);
      toast.success(`Verrouillé sur ${ch?.name}`);
      setLoading(true);
      fetchPlaylist(chId);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "n" || e.key === "ArrowRight") skipNext();
      else if (e.key === "p" || e.key === "ArrowLeft") skipBack();
      else if (e.key === "m") setMuted(m => !m);
      else if (e.key === "r") reshuffle();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [skipNext, reshuffle]);

  const pinned = useMemo(() => allChannels.find(c => c.id === pinnedChannel), [allChannels, pinnedChannel]);

  return (
    <div className="max-w-[1600px] mx-auto px-6 py-8" data-testid="livestream-page">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <span className="relative inline-flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-red-500 animate-ping" />
            <span className="relative rounded-full bg-red-500 w-2 h-2" />
          </span>
          <span className="text-xs uppercase tracking-[0.3em] text-red-400 font-semibold">Direct · 24/7</span>
          {pinned && (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest border"
              style={{ borderColor: pinned.color, color: pinned.color }}
              data-testid="pinned-pill"
            >
              <Pin className="w-3 h-3" /> {pinned.name}
            </span>
          )}
        </div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <h1 className="font-display text-5xl sm:text-6xl leading-none">
            <span className="text-[color:var(--accent)]">LIVE</span> · Le flux continu
          </h1>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]" data-testid="livestream-counter">
            <Tv className="w-3.5 h-3.5 text-[color:var(--accent)]" />
            <span className="text-white font-mono">{playlist.length}</span> vidéos en file
          </div>
        </div>
        <p className="text-[color:var(--text-muted)] mt-3 max-w-2xl">
          Toutes les chaînes Fedevo 2.0 mélangées en un seul flux. Aucune décision à prendre — on s'occupe de la programmation.
        </p>
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
        {/* Player + controls */}
        <div className="space-y-4 min-w-0" data-testid="livestream-player-col">
          {loading || !current ? (
            <div className="aspect-video bg-black border border-[color:var(--border)] rounded-xl flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-[color:var(--accent)]" />
            </div>
          ) : (
            <div className="relative">
              <VideoPlayer key={current.id} video={current} autoplay={true} muted={muted} onEnded={skipNext} />
              <ChannelZap zapKey={current.channel_id} name={current.channel_name} number={current.channel_order != null ? current.channel_order + 1 : undefined} color={current.channel_color} />
              {/* Mini overlay top-left identifying source */}
              <div
                data-testid="livestream-source-pill"
                className="absolute top-3 left-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md text-white text-[10px] uppercase tracking-widest border border-white/10"
              >
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: current.channel_color }} />
                {current.channel_name}
              </div>
            </div>
          )}

          {/* Now playing + controls */}
          {current && (
            <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--bg-elev)] p-4" data-testid="livestream-now-playing">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent)] mb-1">
                    En cours · #{index + 1} / {playlist.length}
                  </div>
                  <h2 className="font-display text-2xl leading-tight line-clamp-2">{current.title}</h2>
                  <Link to={`/watch/${current.id}`} className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] hover:text-[color:var(--accent)] mt-1 inline-block">
                    Voir la page complète →
                  </Link>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={skipBack}
                    disabled={index === 0}
                    data-testid="ls-prev"
                    title="Précédent (P / ←)"
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition disabled:opacity-40"
                  >
                    <SkipBack className="w-4 h-4" />
                  </button>
                  <button
                    onClick={skipNext}
                    data-testid="ls-next"
                    title="Suivant (N / →)"
                    className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[color:var(--accent)] text-black hover:brightness-110 transition shadow-lg"
                  >
                    <SkipForward className="w-5 h-5" />
                  </button>
                  <button
                    onClick={reshuffle}
                    data-testid="ls-shuffle"
                    title="Mélanger (R)"
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition"
                  >
                    <Shuffle className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setMuted(m => !m)}
                    data-testid="ls-mute"
                    title="Mute (M)"
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition"
                  >
                    {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-[color:var(--border)] flex items-center justify-between flex-wrap gap-3 text-xs text-[color:var(--text-muted)]">
                <span className="text-[10px] uppercase tracking-widest">Raccourcis · N suivant · P retour · R mélanger · M son</span>
              </div>
            </div>
          )}

          {/* Up next preview */}
          {nextUp && (
            <Link
              to={`/watch/${nextUp.id}`}
              data-testid="livestream-next-up"
              className="card-hover flex items-center gap-3 rounded-xl border border-[color:var(--border)] bg-[color:var(--bg-elev)] p-3 group"
            >
              {nextUp.thumbnail && (
                <img src={nextUp.thumbnail} alt="" className="w-28 h-16 object-cover rounded shrink-0" loading="lazy" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-muted)] mb-1">
                  Ensuite · #{index + 2}
                </div>
                <div className="text-sm leading-tight line-clamp-2 group-hover:text-[color:var(--accent)] transition">
                  {nextUp.title}
                </div>
                <div className="flex items-center gap-1.5 mt-1 text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: nextUp.channel_color }} />
                  {nextUp.channel_name}
                </div>
              </div>
            </Link>
          )}
        </div>

        {/* Sidebar : pin channel selector + recent queue */}
        <aside className="space-y-4 lg:sticky lg:top-24 min-w-0" data-testid="livestream-sidebar">
          <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--bg-elev)] p-4">
            <div className="flex items-center gap-2 mb-3">
              <Radio className="w-4 h-4 text-[color:var(--accent)]" />
              <h3 className="font-display text-lg">Verrouiller une chaîne</h3>
            </div>
            <p className="text-[10px] text-[color:var(--text-muted)] mb-3 leading-relaxed">
              Cliquez pour ne diffuser qu'une seule source. Re-cliquez pour libérer.
            </p>
            <div className="space-y-1.5">
              {allChannels.map(c => (
                <button
                  key={c.id}
                  onClick={() => togglePin(c.id)}
                  data-testid={`pin-${c.slug}`}
                  className={`w-full inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition text-xs uppercase tracking-widest ${
                    pinnedChannel === c.id
                      ? "bg-[color:var(--accent)]/10 border-[color:var(--accent)] text-[color:var(--accent)]"
                      : "border-[color:var(--border)] hover:border-[color:var(--accent)] text-[color:var(--text-muted)] hover:text-white"
                  }`}
                >
                  {c.logo ? (
                    <img src={c.logo} alt="" className="w-5 h-5 rounded object-contain bg-black/40 p-0.5 shrink-0" />
                  ) : (
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: c.color }} />
                  )}
                  <span className="truncate flex-1 min-w-0 text-left normal-case tracking-normal">{c.name}</span>
                  {pinnedChannel === c.id ? (
                    <Pin className="w-3 h-3" />
                  ) : (
                    <PinOff className="w-3 h-3 opacity-30" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Coming up queue */}
          <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--bg-elev)] p-4">
            <h3 className="font-display text-lg mb-3">À venir</h3>
            <ol className="space-y-1.5" data-testid="livestream-queue">
              {playlist.slice(index + 1, index + 7).map((v, i) => (
                <li key={`${v.id}-${i}`} className="flex items-center gap-2 text-xs">
                  <span className="w-5 text-[10px] font-mono text-[color:var(--text-muted)] shrink-0">{i + 1}.</span>
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: v.channel_color }} />
                  <span className="truncate flex-1 min-w-0 text-[color:var(--text-muted)]">{v.title}</span>
                </li>
              ))}
              {playlist.slice(index + 1).length === 0 && (
                <li className="text-[10px] text-[color:var(--text-muted)] italic">Fin de la file. Mélange auto au prochain skip.</li>
              )}
            </ol>
          </div>
        </aside>
      </div>
    </div>
  );
}
